import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import NotificationBell from "./NotificationBell";
import LanguageSwitcher from "./LanguageSwitcher";
import { useLangPrefix } from "../i18n";
import { resolveImageUrl } from "../utils/imageUrl";

export default function Navbar() {
  const { t } = useTranslation();
  const lp = useLangPrefix();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  if (!user) return null;

  const baseLinks = [
    { to: `${lp}/home`, label: t("nav.home") },
    { to: `${lp}/search`, label: t("nav.search") },
    { to: `${lp}/chat-requests`, label: t("nav.chatRequests") },
    { to: `${lp}/chat`, label: t("nav.chats") },
    { to: `${lp}/settings`, label: t("nav.settings") }
  ];

  const links = user.isAdmin ? [...baseLinks, { to: `${lp}/admin`, label: t("nav.admin") }] : baseLinks;

  const handleLogout = () => {
    logout();
    navigate(`${lp}/login`);
  };

  return (
    <header className="bg-white border-b border-gray-100 sticky top-0 z-40 shadow-sm">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <button className="md:hidden p-2 -ms-2" onClick={() => setMenuOpen((v) => !v)} aria-label={t("nav.menu")}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
            </svg>
          </button>

          <Link to={`${lp}/home`} className="text-2xl font-extrabold text-primary-600">
            قِران
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                  location.pathname.startsWith(l.to)
                    ? "bg-primary-50 text-primary-600"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LanguageSwitcher className="hidden sm:flex" />
            <NotificationBell />
            <Link to={`${lp}/profile/me`} className="hidden sm:flex items-center gap-2">
              <img
                src={resolveImageUrl(user.profileImageUrl) || "https://api.dicebear.com/7.x/initials/svg?seed=" + user.fullName}
                alt={user.fullName}
                className="w-9 h-9 rounded-full object-cover border-2 border-primary-100"
              />
            </Link>
            <button onClick={handleLogout} className="text-sm text-gray-500 hover:text-primary-600 font-bold hidden sm:block">
              {t("nav.logout")}
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setMenuOpen(false)}
              className="block px-5 py-3 text-sm font-bold text-gray-700 border-b border-gray-50"
            >
              {l.label}
            </Link>
          ))}
          <Link to={`${lp}/profile/me`} onClick={() => setMenuOpen(false)} className="block px-5 py-3 text-sm font-bold text-gray-700 border-b border-gray-50">
            {t("nav.myProfile")}
          </Link>
          <div className="px-5 py-3 border-b border-gray-50">
            <LanguageSwitcher />
          </div>
          <button onClick={handleLogout} className="w-full text-right px-5 py-3 text-sm font-bold text-red-500">
            {t("nav.logoutFull")}
          </button>
        </div>
      )}
    </header>
  );
}
